import { useState, type ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { requireAuth } from '../features/auth/guards.js';
import { useIsImpersonating } from '../features/auth/useIsImpersonating.js';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/account/PageHeader.js';
import { BankAccountsSection } from '@/components/account/BankAccountsSection.js';
import { WalletPoolCard } from '@/components/account/WalletPoolCard.js';
import { TransactionRow } from '@/components/account/TransactionRow.js';
import {
  TransferCommissionModal,
  ConvertCashbackModal,
} from '@/components/account/PoolActionModals.js';
import { Card } from '@/components/ui/card.js';
import { cn } from '@/lib/utils/cn.js';
import { Button } from '@/components/ui/button.js';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog.js';
import { Money } from '@/components/money/Money.js';
import { DateRangePicker, type DateRange } from '@/components/ui/date-range-picker.js';
import { formatDateTime, startOfDayIso, endOfDayIso } from '@/lib/datetime/format.js';
import {
  fetchWalletBalances,
  fetchWalletTransactions,
  WALLET_QUERY_KEYS,
  type WalletTransaction,
} from '@/features/wallet/api.js';
import { presentTransaction } from '@/features/wallet/txPresentation.js';
import { ArrowDownToLine, ArrowUpFromLine, Lock } from 'lucide-react';
import { useFormatMoney, useMoneyCurrency } from '@/components/money/MoneyProvider.js';
import {
  fetchMyClaims,
  findActiveClaim,
  freePoolMinor,
  lockedMinorFor,
  turnoverRemainingMinorFor,
  turnoverProgress,
  bigIntish,
  PROMO_QUERY_KEYS,
} from '@/features/promo/api.js';

const walletSearchSchema = z.object({
  tx: z.string().uuid().optional(),
});

export const Route = createFileRoute('/_player/wallet/')({
  beforeLoad: requireAuth,
  validateSearch: walletSearchSchema,
  component: WalletScreen,
});

export const TX_FILTERS = [
  'all',
  'bet',
  'deposit',
  'withdraw',
  'promo',
  'cashback',
  'commission',
] as const;

export type TxFilter = (typeof TX_FILTERS)[number];

/** Chip → the `category` the wallet service filters the ledger by. */
export const FILTER_CATEGORY_MAP: Record<TxFilter, string | undefined> = {
  all:        undefined,
  bet:        'bet',
  deposit:    'deposit',
  withdraw:   'withdraw',
  promo:      'promo',
  cashback:   'cashback',
  commission: 'commission',
};

export const FILTER_LABEL_KEYS: Record<TxFilter, string> = {
  all:        'transactions.filter.all',
  bet:        'transactions.filter.bets',
  deposit:    'transactions.filter.deposits',
  withdraw:   'transactions.filter.withdrawals',
  promo:      'transactions.filter.promos',
  cashback:   'transactions.filter.cashback',
  commission: 'transactions.filter.commission',
};

const POOL_LABEL_KEYS: Record<string, string> = {
  cash:       'pools.cash',
  promotion:  'pools.bonus',
  commission: 'pools.commission',
  cashback:   'pools.cashback',
  freegame:   'detail.poolFreegame',
};

// Internal refs are colon-delimited UUIDs. Players only ever need a short,
// support-quotable code, so we surface the ledger row id.
function txRefCode(tx: WalletTransaction): string {
  return `#${tx.id.slice(0, 8).toUpperCase()}`;
}

function TxSkeleton() {
  return (
    <div className="flex flex-col gap-1">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-14 bg-[var(--bg-elevated)]/50 animate-pulse rounded" />
      ))}
    </div>
  );
}

type PoolActionModal = 'transfer-commission' | 'convert-cashback' | null;

interface MoneyActionLinkProps {
  to: '/wallet/deposit' | '/wallet/withdraw';
  icon: ReactNode;
  label: string;
  hint: string;
  className: string;
}

function MoneyActionLink({ to, icon, label, hint, className }: MoneyActionLinkProps) {
  return (
    <Link
      to={to}
      className={cn(
        'flex min-h-14 min-w-0 items-center gap-2.5 rounded-xl px-3 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_4px_12px_rgba(0,0,0,0.3)] transition-[filter,scale] duration-200 hover:brightness-110 motion-safe:active:scale-[0.98] outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-raised)] focus-visible:ring-[var(--text-primary)]',
        className,
      )}
    >
      <span aria-hidden className="grid size-8 shrink-0 place-items-center rounded-full bg-current/15">
        {icon}
      </span>
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="text-[15px] font-bold">{label}</span>
        <span className="text-[11px] font-medium opacity-80">{hint}</span>
      </span>
    </Link>
  );
}

function WalletScreen() {
  const { t, i18n } = useTranslation('wallet');
  const locale = i18n.language || 'th';
  const navigate = Route.useNavigate();
  const { tx: txId } = Route.useSearch();
  const [filter, setFilter] = useState<TxFilter>('all');
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [poolModal, setPoolModal] = useState<PoolActionModal>(null);
  const isImpersonating = useIsImpersonating();

  const walletQuery = useQuery({
    queryKey: WALLET_QUERY_KEYS.balances,
    queryFn: fetchWalletBalances,
    // SSE (useWalletStream in AppShell) drives live updates; this is the safety-net poll.
    staleTime: 30 * 60_000,
    refetchInterval: 30 * 60_000,
    refetchOnWindowFocus: false,
  });

  // Bonus-pool gating: surface an active turnover-in-progress claim so the
  // bonus tile reads "locked until turnover complete" instead of looking
  // freely withdrawable.
  const myClaimsQuery = useQuery({
    queryKey: PROMO_QUERY_KEYS.myClaims({ status: 'active' }),
    queryFn: () => fetchMyClaims({ status: 'active' }),
    staleTime: 60_000,
  });
  const activeClaims = myClaimsQuery.data?.items ?? [];
  // Pool-scoped. The banner above is about BONUS principal — it is what
  // `activate-bonus` holds back and what refuses a withdrawal (FIN-117) — and a
  // turnover-locked cashback credit does neither. Unscoped, a locked rebate
  // credit raised a banner saying the player's withdrawals were blocked when
  // they were not.
  const activeClaim = findActiveClaim(activeClaims, 'promotion');
  const activeClaimProgress = activeClaim ? turnoverProgress(activeClaim) : 0;
  // What the cashback tile and the Convert modal are allowed to offer. Display
  // only: svc-wallet re-derives this from svc-promo under the wallet row lock
  // and answers `WALLET-PA-007`, so this cap can lag — it exists to stop the
  // player typing an amount that will be refused, never to authorise one.
  const lockedCashbackMinor = lockedMinorFor(activeClaims, 'cashback');
  // What the player still has to BET, which is what the tile and the modal tell
  // them to do. `lockedCashbackMinor` is what is being held; on a multiplier of
  // 2 it is half this figure, and quoting it reads as a smaller task than the
  // one the convert gate is waiting on.
  const cashbackTurnoverOwed = turnoverRemainingMinorFor(activeClaims, 'cashback');

  // The date range goes to the SERVER. Filtering it in the browser only ever
  // narrowed the 50 rows that happened to be loaded, so a player picking last
  // month saw an empty list for a period they certainly transacted in — the
  // same defect `/history` carried (WEB-23). The boundaries have to be explicit:
  // a bare `Date` from the picker is midnight local, which would drop everything
  // the player did on the last day they chose.
  const fromIso = dateRange?.from ? startOfDayIso(dateRange.from) : undefined;
  const toIso = dateRange?.to ? endOfDayIso(dateRange.to) : undefined;

  const txQuery = useInfiniteQuery({
    queryKey: WALLET_QUERY_KEYS.transactions(filter, { from: fromIso, to: toIso }),
    queryFn: ({ pageParam = 1 }) =>
      fetchWalletTransactions({
        page: pageParam as number,
        pageSize: 50,
        category: FILTER_CATEGORY_MAP[filter],
        ...(fromIso ? { from: fromIso } : {}),
        ...(toIso ? { to: toIso } : {}),
      }),
    initialPageParam: 1,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((s, p) => s + p.items.length, 0);
      if (loaded >= last.total) return undefined;
      return pages.length + 1;
    },
    // SSE transaction.appended events invalidate this query on new transactions.
    staleTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });

  const wallet = walletQuery.data;
  // currency is provided by the MoneyProvider at AppShell; derive locally only
  // for explicit overrides (e.g. historical tx rows that carry their own currency).
  const currency = useMoneyCurrency();
  const formatMoney = useFormatMoney();

  const txItems: WalletTransaction[] = (txQuery.data?.pages ?? []).flatMap((p) => p.items);

  // Resolve selected tx from URL search param
  const selectedTx = txId ? (txItems.find((tx) => tx.id === txId) ?? null) : null;

  function openTx(tx: WalletTransaction) {
    void navigate({ search: { tx: tx.id } });
  }
  function closeTx() {
    void navigate({ search: { tx: undefined } });
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        onBack={() => void navigate({ to: '/' })}
      />

      {/* Summary banner */}
      <Card className="p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-1">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)] mb-1">
            {t('totalBalance')}
          </div>
          {walletQuery.isPending ? (
            <div className="h-9 w-36 bg-[var(--bg-elevated)]/50 animate-pulse rounded" />
          ) : walletQuery.isError ? (
            <div className="text-[var(--danger)] text-sm">{t('errors.failedToLoadBalance')}</div>
          ) : (
            <>
              <div className="text-3xl font-extrabold bf-tnum text-[var(--text-primary)]">
                <Money minor={wallet!.balance} />
              </div>
              <div className="font-mono text-[11px] text-[var(--text-muted)] mt-1">
                {t('summary.withdrawable')}{' '}
                <span className="text-[var(--success)]">
                  <Money minor={wallet!.withdrawable} />
                </span>
              </div>
            </>
          )}
        </div>
        {!isImpersonating && (
          <div className="grid grid-cols-2 gap-3 w-full sm:w-auto sm:min-w-[360px]">
            <MoneyActionLink
              to="/wallet/deposit"
              icon={<ArrowDownToLine size={18} aria-hidden />}
              label={t('actions.deposit')}
              hint={t('actions.depositHint')}
              className="bg-[var(--success)] text-[var(--on-status)]"
            />
            <MoneyActionLink
              to="/wallet/withdraw"
              icon={<ArrowUpFromLine size={18} aria-hidden />}
              label={t('actions.withdraw')}
              hint={t('actions.withdrawHint')}
              className="bg-[var(--text-primary)] text-[var(--bg)]"
            />
          </div>
        )}
      </Card>

      {/* Locked-bonus banner — only when an active claim has turnover left */}
      {activeClaim && (
        <div
          className="flex items-center justify-between gap-3 rounded-xl border border-[var(--warning-bg)] bg-[var(--warning-bg)]/30 px-4 py-3"
          role="status"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Lock className="w-4 h-4 text-[var(--warning)] shrink-0" aria-hidden />
            <div className="min-w-0">
              <div className="text-[13px] font-bold text-[var(--text-primary)]">
                {t('bonusLocked.title')}
              </div>
              <div className="text-[11px] text-[var(--text-secondary)] font-mono">
                {t('bonusLocked.progress', {
                  achieved: formatMoney(bigIntish(activeClaim.turnoverAchieved), currency),
                  required: formatMoney(bigIntish(activeClaim.turnoverRequired), currency),
                  percent: Math.round(activeClaimProgress * 100),
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 4 pool cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {walletQuery.isPending ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-24 bg-[var(--bg-elevated)]/50 animate-pulse rounded-xl" />
          ))
        ) : wallet ? (
          <>
            {(['cash', 'bonus', 'commission', 'cashback'] as const).map((variant) => {
              const amountMinor =
                variant === 'cash'       ? wallet.creditCash :
                variant === 'bonus'      ? wallet.creditPromotion :
                variant === 'commission' ? wallet.commissionWallet :
                                           wallet.cashbackWallet;

              // The cashback tile shows its own split when part of the pool is
              // still wagering, because the headline figure alone reads as
              // money the player can take right now and a Convert for that
              // amount is refused.
              const note =
                variant === 'cashback' && lockedCashbackMinor > 0n
                  ? t('cashbackLocked.split', {
                      free: formatMoney(
                        freePoolMinor(wallet.cashbackWallet, lockedCashbackMinor),
                        currency,
                      ),
                      turnoverRemaining: formatMoney(cashbackTurnoverOwed, currency),
                    })
                  : undefined;

              // Pool-specific action callbacks. The bonus tile points at the
              // promotions page: bonus credit is released by finishing the
              // promotion's turnover, so there is nothing for the player to
              // press here — the button that used to sit there could only
              // refuse them.
              const onAction =
                variant === 'cash'       ? () => navigate({ to: '/wallet/deposit' }) :
                variant === 'bonus'      ? () => navigate({ to: '/promos' }) :
                variant === 'commission' ? () => setPoolModal('transfer-commission') :
                                           () => setPoolModal('convert-cashback');

              return (
                <WalletPoolCard
                  key={variant}
                  variant={variant}
                  amountMinor={amountMinor}
                  currency={currency}
                  note={note}
                  onAction={onAction}
                />
              );
            })}
          </>
        ) : null}
      </div>

      <BankAccountsSection />

      {/* Pool action modals */}
      <TransferCommissionModal
        open={poolModal === 'transfer-commission'}
        onClose={() => setPoolModal(null)}
        maxMinor={wallet?.commissionWallet ?? 0}
        currency={currency}
      />
      <ConvertCashbackModal
        open={poolModal === 'convert-cashback'}
        onClose={() => setPoolModal(null)}
        maxMinor={freePoolMinor(wallet?.cashbackWallet ?? 0, lockedCashbackMinor)}
        lockedMinor={lockedCashbackMinor}
        turnoverRemainingMinor={cashbackTurnoverOwed}
        currency={currency}
      />

      {/* Transactions */}
      <section className="flex flex-col gap-3">
        <h2 className="text-[15px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
          {t('transactions.title')}
        </h2>
        <DateRangePicker
          value={dateRange}
          onChange={setDateRange}
          label={t('transactions.dateRange')}
        />

        <div className="flex flex-wrap items-center gap-2">
          {TX_FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={[
                'h-8 px-3 rounded-full text-[12px] font-semibold border transition-colors',
                f === filter
                  ? 'bg-[var(--primary)] text-[var(--primary-on)] border-[var(--primary)]'
                  : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]',
              ].join(' ')}
            >
              {t(FILTER_LABEL_KEYS[f])}
            </button>
          ))}
        </div>

        {/* TX list */}
        <Card className="overflow-hidden">
          {txQuery.isPending ? (
            <div className="p-4"><TxSkeleton /></div>
          ) : txQuery.isError ? (
            <div className="py-10 text-center text-[var(--danger)] text-[13px] flex flex-col items-center gap-3">
              {t('errors.failedToLoadTransactions')}
              <Button size="sm" variant="secondary" onClick={() => txQuery.refetch()}>{t('common.retry')}</Button>
            </div>
          ) : txItems.length === 0 ? (
            <div className="py-10 text-center text-[var(--text-muted)] text-[13px]">
              {t('transactions.empty')}
            </div>
          ) : (
            <>
              {txItems.map((tx, i) => {
                const { icon, labelKey, direction } = presentTransaction(tx);
                const tone = direction > 0 ? 'credit' : direction < 0 ? 'debit' : 'neutral';
                return (
                  <TransactionRow
                    key={tx.id}
                    icon={icon}
                    title={t(labelKey)}
                    subtitle={formatDateTime(tx.createdAt, locale)}
                    amountMinor={tx.amount}
                    currency={tx.currency ?? currency}
                    tone={tone}
                    onClick={() => openTx(tx)}
                    hasBorder={i > 0}
                  />
                );
              })}
              {txQuery.hasNextPage && (
                <div className="p-3 border-t border-[var(--border-subtle)] text-center">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => txQuery.fetchNextPage()}
                    disabled={txQuery.isFetchingNextPage}
                  >
                    {txQuery.isFetchingNextPage ? t('common.loading') : t('common.loadMore')}
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>
      </section>

      {/* Detail drawer */}
      <Dialog open={!!selectedTx} onOpenChange={(v) => { if (!v) closeTx(); }}>
        <DialogContent className="max-w-sm flex flex-col gap-3">
          {selectedTx && (
          <>
            <DialogHeader>
              <DialogTitle>{t('detail.title')}</DialogTitle>
            </DialogHeader>
            {(() => {
              const { labelKey, direction } = presentTransaction(selectedTx);
              const isCredit = direction > 0;
              const sign = isCredit ? '+' : direction < 0 ? '−' : '';
              const amtClass = isCredit ? 'text-[var(--success)]' : 'text-[var(--text-primary)]';
              const poolKey = POOL_LABEL_KEYS[selectedTx.pool];
              const rows: [string, string, ReactNode][] = [
                ['type', t('detail.type'), t(labelKey)],
                ['pool', t('detail.pool'), poolKey ? t(poolKey) : selectedTx.pool],
                [
                  'amount',
                  t('detail.amount'),
                  <span className={`font-bold ${amtClass}`}>
                    {sign ? `${sign} ` : ''}
                    <Money minor={Math.abs(selectedTx.amount)} currency={selectedTx.currency ?? currency} />
                  </span>,
                ],
                ['time', t('detail.time'), formatDateTime(selectedTx.createdAt, locale)],
                ['ref', t('detail.reference'), <span className="font-mono">{txRefCode(selectedTx)}</span>],
              ];
              return (
                <div className="flex flex-col gap-2 text-[13px]">
                  {rows.map(([key, label, value]) => (
                    <div key={key} className="flex justify-between gap-3 py-1.5 border-b border-[var(--border-subtle)] last:border-0">
                      <span className="text-[var(--text-muted)]">{label}</span>
                      <span className="font-semibold text-right">{value}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
            <Button variant="secondary" onClick={() => closeTx()}>
              {t('detail.close')}
            </Button>
          </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
